import React, { Fragment, useEffect, useState } from 'react';
import useFieldTracker from '../hooks/useFieldTracker';
import VgsCollectValue from './VgsInput/VgsCollectValue';
import { FieldError } from './VgsInput/field';
import config from '../util/config';

export default function Dob({
  form,
  entityName,
  onInvalidChange,
  dobApiError,
  fieldErrors,
}: {
  form: VGSForm<unknown>;
  entityName: string;
  onInvalidChange: (fieldName: string, isInvalid: boolean) => void;
  dobApiError?: ApiError;
  fieldErrors: FieldError[];
}): JSX.Element {
  const [emptyFields, onEmptyChange] = useFieldTracker([
    `${entityName}DobMonth`,
    `${entityName}DobDay`,
    `${entityName}DobYear`,
  ]);
  const [invalidFields, onInvalidFieldChange] = useFieldTracker([]);
  const [isValid, setIsValid] = useState(false);
  const [dobYearValidations, setDobYearValidations] = useState<RegExp[]>([]);

  // DOB year must be between 120 years ago and 18 years ago
  useEffect(() => {
    if (config.environment === 'prod' && dobYearValidations.length === 0) {
      const currentYear = new Date().getFullYear();
      const earliestYear = currentYear - 120;
      const latestYear = currentYear - 18;
      const years = [];
      for (let year = earliestYear; year <= latestYear; year++) {
        years.push(year);
      }
      setDobYearValidations([new RegExp(years.join('|'))]);
    }
  }, [dobYearValidations.length]);

  const sharedProps = {
    type: 'text' as VgsFieldType,
    required: true,
    onEmptyChange,
    onInvalidChange: onInvalidFieldChange,
    form,
    hideEditButton: true,
    fieldErrors,
  };

  const containerProps = {
    className: 'px-1 hide-edit-button md:w-1/3',
  };

  useEffect(() => {
    onInvalidChange(`${entityName}Dob`, !isValid);
  });

  useEffect(() => {
    const newValidity = emptyFields.length === 0 && invalidFields.length === 0;
    if (newValidity !== isValid) {
      setIsValid(newValidity);
      onInvalidChange(`${entityName}Dob`, !newValidity);
    }
  }, [emptyFields, entityName, invalidFields, isValid, onInvalidChange]);

  return (
    <Fragment>
      <div {...containerProps}>
        <VgsCollectValue
          name={`${entityName}DobMonth`}
          label='DOB'
          placeholder='MM'
          maskInput={{
            mask: 'MM',
            maskChar: null,
            formatChar: { M: '[0-9]' },
          }}
          validations={[/^(0[1-9]|1[0-2])$/]}
          showRequiredAsterisk={true}
          {...sharedProps}
        />
      </div>
      <div {...containerProps}>
        <VgsCollectValue
          name={`${entityName}DobDay`}
          label=''
          placeholder='DD'
          maskInput={{
            mask: 'DD',
            maskChar: null,
            formatChar: { D: '[0-9]' },
          }}
          validations={[/^(0[1-9]|[1-2]\d|3[0-1])$/]}
          showRequiredAsterisk={false}
          {...sharedProps}
        />
      </div>
      <div {...containerProps}>
        <VgsCollectValue
          name={`${entityName}DobYear`}
          label=''
          placeholder='YYYY'
          maskInput={{
            mask: 'YYYY',
            maskChar: null,
            formatChar: { Y: '[0-9]' },
          }}
          validations={dobYearValidations}
          showRequiredAsterisk={false}
          {...sharedProps}
        />
      </div>
      {dobApiError !== undefined && (
        <span className='px-1 text-sm text-red-600'>{dobApiError.message}</span>
      )}
    </Fragment>
  );
}
